import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { cn } from "../../lib/utils";


const MouseEnterContext = createContext(undefined);


/* =========================================================
   CARD CONTAINER
========================================================= */

export const CardContainer = ({
  children,
  className,
  containerClassName,
}) => {

  const containerRef = useRef(null);

  const [isMouseEntered, setIsMouseEntered] =
    useState(false);


  const handleMouseMove = (e) => {

    if (!containerRef.current) return;

    const {
      left,
      top,
      width,
      height,
    } = containerRef.current.getBoundingClientRect();


    const x =
      (e.clientX - left - width / 2) / 25;

    const y =
      (e.clientY - top - height / 2) / 25;


    containerRef.current.style.transform =
      `rotateY(${x}deg) rotateX(${y}deg)`;

  };


  const handleMouseEnter = () => {
    setIsMouseEntered(true);
  };


  const handleMouseLeave = () => {

    setIsMouseEntered(false);

    if (!containerRef.current) return;

    containerRef.current.style.transform =
      "rotateY(0deg) rotateX(0deg)";

  };


  return (

    <MouseEnterContext.Provider
      value={[
        isMouseEntered,
        setIsMouseEntered,
      ]}
    >

      <div
        className={cn(
          "flex items-center justify-center",
          containerClassName
        )}
        style={{
          perspective: "1000px",
        }}
      >

        <div
          ref={containerRef}
          onMouseEnter={handleMouseEnter}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className={cn(
            `
              relative
              flex
              items-center
              justify-center
              transition-all
              duration-200
              ease-linear
            `,
            className
          )}
          style={{
            transformStyle: "preserve-3d",
          }}
        >

          {children}

        </div>

      </div>

    </MouseEnterContext.Provider>

  );

};



/* =========================================================
   CARD BODY
========================================================= */

export const CardBody = ({
  children,
  className,
}) => {

  return (

    <div
      className={cn(
        `
          h-96
          w-96
          [transform-style:preserve-3d]
          [&>*]:[transform-style:preserve-3d]
        `,
        className
      )}
    >

      {children}

    </div>

  );

};



/* =========================================================
   CARD ITEM
========================================================= */

export const CardItem = ({
  as: Tag = "div",
  children,
  className,

  translateX = 0,
  translateY = 0,
  translateZ = 0,

  rotateX = 0,
  rotateY = 0,
  rotateZ = 0,

  ...rest
}) => {

  const ref = useRef(null);

  const [isMouseEntered] =
    useMouseEnter();


  useEffect(() => {

    if (!ref.current) return;


    if (isMouseEntered) {

      ref.current.style.transform = `
        translateX(${translateX}px)
        translateY(${translateY}px)
        translateZ(${translateZ}px)
        rotateX(${rotateX}deg)
        rotateY(${rotateY}deg)
        rotateZ(${rotateZ}deg)
      `;

    } else {

      ref.current.style.transform = `
        translateX(0px)
        translateY(0px)
        translateZ(0px)
        rotateX(0deg)
        rotateY(0deg)
        rotateZ(0deg)
      `;

    }

  }, [
    isMouseEntered,
    translateX,
    translateY,
    translateZ,
    rotateX,
    rotateY,
    rotateZ,
  ]);


  return (

    <Tag
      ref={ref}
      className={cn(
        `
          w-fit
          transition
          duration-200
          ease-linear
        `,
        className
      )}
      {...rest}
    >

      {children}

    </Tag>

  );

};



/* =========================================================
   HOOK
========================================================= */

export const useMouseEnter = () => {

  const context =
    useContext(MouseEnterContext);


  if (context === undefined) {

    throw new Error(
      "useMouseEnter must be used inside CardContainer"
    );

  }


  return context;

};